"use client";

import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Herobusinesslitigation() {
  const prefersReducedMotion = useReducedMotion();
  return <div className="bg-background text-foreground overflow-hidden">
      {}
      <section data-section-id="biz-lit-hero" id="herobusinesslitigation" className="relative bg-background text-foreground py-16 lg:py-24 px-6 md:px-12 lg:px-20">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={prefersReducedMotion ? false : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="flex flex-col items-start">
            <span className="text-xs uppercase tracking-widest text-secondary font-semibold block mb-4">
              BUSINESS LITIGATION, SALES & ACQUISITIONS
            </span>

            <h1 className="font-serif text-4xl sm:text-5xl lg:text-[45px] leading-[1.08] font-semibold tracking-tight mb-6 text-foreground">
              Business Disputes and Transactions Informed by Decades of Experience
            </h1>

            <p className="text-lg font-sans text-muted-foreground leading-relaxed max-w-lg mb-10">
              The firm advises South Florida business owners in selected business disputes, purchases and sales of businesses, ownership matters, corporate transactions, and related agreements.
            </p>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-8 py-4 rounded-md text-base transition-colors">
                <Link to="/ContactUsPage">
                  Discuss Your Business Matter
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
            </div>
          </motion.div>

          <motion.div initial={prefersReducedMotion ? false : {
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.7,
          delay: 0.2,
          ease: "easeOut"
        }} className="p-6 bg-muted/10 rounded-lg">
            <h3 className="font-serif text-xl font-medium tracking-tight text-foreground mb-6 pb-4 border-b border-border/60">
              BUSINESS EXPERIENCE
            </h3>
            
            <ul className="space-y-4 font-sans text-foreground text-sm md:text-base">
              <li className="flex items-start">
                <span className="text-secondary font-semibold mr-3">—</span>
                <div>
                  <span className="font-semibold block text-foreground">Since 1983</span>
                  <span className="text-xs text-muted-foreground">Established Practice</span>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-secondary font-semibold mr-3">—</span>
                <span>Business Purchases & Sales</span>
              </li>
              <li className="flex items-start">
                <span className="text-secondary font-semibold mr-3">—</span>
                <span>Corporate Agreements & Governance</span>
              </li>
              <li className="flex items-start">
                <span className="text-secondary font-semibold mr-3">—</span>
                <span>Selected Commercial Disputes</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </section>

      {}
      <section className="py-20 lg:py-28 px-6 md:px-10 bg-background border-b border-border/60">
        <div className="max-w-[1280px] mx-auto">
          <div className="max-w-3xl mb-12">
            <span className="text-xs uppercase tracking-widest text-secondary font-semibold block mb-3">
              BUSINESS MATTERS
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-6">
              When a Business Is Changing—or a Business Relationship Breaks Down
            </h2>
            <p className="text-muted-foreground font-sans text-lg leading-relaxed">
              Business matters may involve a planned transaction, a change in ownership, a disagreement among principals, or a dispute over contracts, governance, or business operations. The firm evaluates these matters by first identifying the governing documents, ownership structure, relevant communications, and practical objectives.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-stone-50 border border-stone-100 rounded-lg p-8 shadow-sm">
              <h3 className="font-serif text-2xl font-medium text-foreground mb-4">
                Business Sales & Acquisitions
              </h3>
              <p className="text-muted-foreground font-sans leading-relaxed">
                The firm assists with the purchase and sale of businesses, mergers and acquisitions, due diligence, ownership transfers, internal restructuring, and related transaction documents.
              </p>
            </div>

            <div className="bg-stone-50 border border-stone-100 rounded-lg p-8 shadow-sm">
              <h3 className="font-serif text-2xl font-medium text-foreground mb-4">
                Business Litigation & Disputes
              </h3>
              <p className="text-muted-foreground font-sans leading-relaxed">
                The firm handles selected disputes involving business owners, partners, shareholders, contracts, ownership interests, business separation, and related commercial matters.
              </p>
            </div>
          </div>
        </div>
      </section>

      {}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-background border-b border-border/60">
        <div className="max-w-[1280px] mx-auto">
          <div className="max-w-3xl mb-16">
            <span className="text-xs uppercase tracking-widest text-secondary font-semibold block mb-3">
              SALES & ACQUISITIONS
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Buying, Selling and Restructuring Businesses
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[{
            num: "01",
            title: "Purchase & Sale of Businesses",
            desc: "Review and preparation of documents for asset purchases, stock purchases, and related business transactions."
          }, {
            num: "02",
            title: "Due Diligence",
            desc: "Comprehensive review of corporate records, financial standing, contracts, and potential exposure points."
          }, {
            num: "03",
            title: "Mergers & Acquisitions",
            desc: "Legal support for business acquisitions and combinations, including transaction structure, document review, and closing-related matters."
          }, {
            num: "04",
            title: "Corporate Restructuring",
            desc: "Assistance with changes in entity structure, ownership arrangements, and the creation or dissolution of business entities."
          }, {
            num: "05",
            title: "Stock & Ownership Transactions",
            desc: "Preparation and review of documents relating to the issuance, purchase, sale, or transfer of ownership interests."
          }].map((item, index) => <div key={index} data-index={index} className="bg-card border border-border/80 rounded-lg p-8 shadow-sm flex flex-col justify-between">
                <div>
                  <span className="text-xs font-mono text-secondary font-semibold tracking-wider block mb-4">
                    {item.num}
                  </span>
                  <h3 className="font-serif text-xl font-medium text-foreground mb-3">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed font-sans">
                    {item.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-background border-b border-border/60">
        <div className="max-w-[1280px] mx-auto">
          <div className="max-w-3xl mb-16">
            <span className="text-xs uppercase tracking-widest text-secondary font-semibold block mb-3">
              BUSINESS DISPUTES
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Disputes Between Owners, Partners and Business Interests
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[{
            num: "01",
            title: "Partner & Principal Disputes",
            desc: "Disputes involving business partners, principals, or other owners concerning control, management, ownership, or the future of the business."
          }, {
            num: "02",
            title: "Shareholder & Ownership Disputes",
            desc: "Matters involving shareholder rights, ownership interests, transfers, control, and related governance issues."
          }, {
            num: "03",
            title: "Contract Disputes",
            desc: "Enforcing commercial agreements and defending against claims of breach or misinterpretation."
          }, {
            num: "04",
            title: "Business Separation",
            desc: "Negotiating clean breakups, asset divisions, and corporate dissolutions when partnerships end."
          }, {
            num: "05",
            title: "Pre-Litigation Negotiation",
            desc: "Evaluation and negotiation of business disputes before litigation when a practical resolution may be possible."
          }, {
            num: "06",
            title: "Litigation & Settlement",
            desc: "Representing commercial clients in court when a dispute cannot be resolved through negotiation."
          }].map((item, index) => <div key={index} data-index={index} className="bg-card border border-border/80 rounded-lg p-8 shadow-sm flex flex-col justify-between">
                <div>
                  <span className="text-xs font-mono text-secondary font-semibold tracking-wider block mb-4">
                    {item.num}
                  </span>
                  <h3 className="font-serif text-xl font-medium text-foreground mb-3">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed font-sans">
                    {item.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-background border-b border-border/60">
        <div className="max-w-[1280px] mx-auto">
          <div className="max-w-3xl mb-16">
            <span className="text-xs uppercase tracking-widest text-secondary font-semibold block mb-3">
              CORPORATE GOVERNANCE
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Documents That Support Day-to-Day Corporate Operations
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[{
            num: "01",
            title: "Corporate Minutes",
            desc: "Preparation of minutes documenting actions taken at board, shareholder, or other formal company meetings."
          }, {
            num: "02",
            title: "Corporate Resolutions",
            desc: "Drafting formal written determinations required by banking, regulatory, and corporate bodies."
          }, {
            num: "03",
            title: "Board Meetings",
            desc: "Advising directors on procedural requirements, voting protocols, and fiduciary responsibilities."
          }, {
            num: "04",
            title: "Shareholder Meetings",
            desc: "Preparing notices, proxies, and meeting records aligned with corporate bylaws and Florida law."
          }, {
            num: "05",
            title: "Issuance & Transfer of Shares",
            desc: "Documentation relating to the issuance, purchase, sale, or transfer of shares and other ownership interests."
          }, {
            num: "06",
            title: "Shareholder & Governance Agreements",
            desc: "Preparation and review of agreements governing ownership relationships, rights, responsibilities, and company operations."
          }].map((item, index) => <div key={index} data-index={index} className="bg-card border border-border/80 rounded-lg p-8 shadow-sm flex flex-col justify-between">
                <div>
                  <span className="text-xs font-mono text-secondary font-semibold tracking-wider block mb-4">
                    {item.num}
                  </span>
                  <h3 className="font-serif text-xl font-medium text-foreground mb-3">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed font-sans">
                    {item.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-background border-b border-border/60">
        <div className="max-w-[1280px] mx-auto">
          <div className="max-w-3xl mb-16">
            <span className="text-xs uppercase tracking-widest text-secondary font-semibold block mb-3">
              THE APPROACH
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-6">
              Begin with the Governing Documents and the Facts
            </h2>
            <p className="text-muted-foreground font-sans text-lg leading-relaxed">
              A business dispute or transaction should be evaluated in light of the governing agreements, ownership structure, relevant communications, corporate records, available evidence, practical objectives, and cost of proceeding.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[{
            num: "01",
            title: "Identify the Governing Documents",
            desc: "Reviewing bylaws, operating agreements, contracts, and corporate records to understand the governing framework."
          }, {
            num: "02",
            title: "Clarify the Facts and Objectives",
            desc: "Gathering timeline information and establishing realistic, commercial goals for the client."
          }, {
            num: "03",
            title: "Evaluate the Available Options",
            desc: "Weighing transaction pathways or litigation avenues with clear focus on cost-effectiveness."
          }].map((item, index) => <div key={index} data-index={index} className="bg-card border border-border/80 rounded-lg p-8 shadow-sm">
                <span className="text-xs font-mono text-secondary font-semibold tracking-wider block mb-4">
                  {item.num}
                </span>
                <h3 className="font-serif text-xl font-medium text-foreground mb-3">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed font-sans">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {}
      <section className="py-24 md:py-36 px-6 md:px-12 lg:px-20 bg-background border-t border-border text-foreground text-center">
        <div className="max-w-3xl mx-auto">
          <span className="text-xs uppercase tracking-widest text-secondary font-semibold block mb-3">
            THE NEXT STEP
          </span>
          <h2 className="font-serif text-3xl md:text-5xl font-bold tracking-tight mb-6">
            Discuss the Business Matter
          </h2>
          <p className="text-muted-foreground font-sans text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            Whether the issue involves a proposed transaction, ownership change, contractual dispute, or disagreement among business principals, the first step is to identify the relevant documents, parties, facts, and objectives.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-8 py-4 rounded-md text-base transition-colors">
              <Link to="/ContactUsPage">
                Request a Consultation
                <ArrowRight className="size-4 ml-2" />
              </Link>
            </Button>
            <Link to="/ContactUsPage" className="text-sm font-medium tracking-wider uppercase text-foreground hover:text-secondary transition-colors underline underline-offset-4">
              Contact the Office
            </Link>
          </div>
        </div>
      </section>
    </div>;
}